/**
 * Tracks UTxO references consumed by in-flight deposit transactions
 * so that subsequent deposits don't try to spend the same UTxOs.
 *
 * Each entry is keyed by "txHash#index" and stores an expiry timestamp
 * derived from the transaction's TTL plus a small buffer.
 */

import type { Transaction, TransactionUnspentOutput } from "@blaze-cardano/core";

/** Buffer added after the tx TTL before we release the UTxOs. */
const EXPIRY_BUFFER_MS = 30_000;

const inFlight = new Map<string, number>();

function utxoKey(txHash: string, index: bigint | number): string {
  return `${txHash}#${index}`;
}

/**
 * Extract input references from a built transaction and mark them in-flight.
 * @param tx - The built transaction
 * @param ttlUnixMs - The transaction's TTL as a unix timestamp in milliseconds
 */
export function markTransactionInputsInFlight(tx: Transaction, ttlUnixMs: number) {
  const expiresAt = ttlUnixMs + EXPIRY_BUFFER_MS;
  for (const input of tx.body().inputs().values()) {
    inFlight.set(utxoKey(input.transactionId(), input.index()), expiresAt);
  }
}

/**
 * Release all in-flight UTxO references (e.g. when a deposit settles or fails).
 */
export function releaseAllInFlight() {
  inFlight.clear();
}

/**
 * Filter out UTxOs that are currently in-flight from a list.
 * Prunes expired entries automatically.
 */
export function filterInFlightUtxos(
  utxos: TransactionUnspentOutput[],
): TransactionUnspentOutput[] {
  const now = Date.now();
  // Prune expired entries
  for (const [key, expiresAt] of inFlight) {
    if (now >= expiresAt) inFlight.delete(key);
  }
  if (inFlight.size === 0) return utxos;
  return utxos.filter(
    (u) => !inFlight.has(utxoKey(u.input().transactionId(), u.input().index())),
  );
}
